/**
 * ExtensionInstallModal — janela exibida quando o usuário aciona um recurso que exige a extensão
 * (captura, enhance) e ela não está presente, no visual da proposta "Elements" (17/10/2026):
 * três passos, "Instalar na Chrome Web Store", "Já instalei" (verifica de novo) e, se ainda não
 * encontrou, o aviso com "Recarregar a página".
 * Controlado por uiStore (activeModal === EXTENSION_MODAL_ID).
 *
 * Movimento (23/10/2026): a janela entra e sai com .ds-veu/.ds-dialogo; "Já instalei" passa ao
 * giro com "Verificando…" depois de 200 ms e, achada a extensão, ao ✓ no mesmo lugar (.ds-troca)
 * enquanto a janela sai; sem a extensão, o aviso abre espaço (.ds-altura). Enquanto verifica, o
 * botão fica com aria-disabled (continua focável; clicar de novo não faz nada).
 */
import { useState } from 'react';
import { useBrand } from '../../hooks/useBrand';
import { useUiStore } from '../../stores/uiStore';
import { useExtensionPresence } from '../../hooks/useExtensionPresence';
import { EXTENSION_MODAL_ID } from '../../hooks/useRequireExtension';
import { emitActivity } from '../../lib/activity';
import { AbreNoLugar, Html, RotuloCarregando, ScreenDialog } from '../screen/Screen';
import { useScreenText } from '../screen/screenText';

export function ExtensionInstallModal(): JSX.Element | null {
  const { tx } = useScreenText();
  const brand = useBrand();
  const activeModal = useUiStore((s) => s.activeModal);
  const closeModal = useUiStore((s) => s.closeModal);
  // O AppShell já mantém o watcher global. Aqui precisamos apenas do recheck acionado pelo
  // botão; montar outro watcher duplicaria probes e listeners de focus/pageshow.
  const { recheck } = useExtensionPresence(false);
  const [checking, setChecking] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [found, setFound] = useState(false);
  const open = activeModal === EXTENSION_MODAL_ID;
  // O ✓ de "achei" vale para a saída desta abertura; ao abrir de novo, volta o "Já instalei".
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setFound(false);
  }

  async function handleRecheck(): Promise<void> {
    // Verificando (ou achada, enquanto a janela sai): clicar de novo não faz nada.
    if (checking || found) return;
    setChecking(true);
    setNotFound(false);
    const ok = await recheck();
    setChecking(false);
    if (ok) {
      setFound(true);
      closeModal();
    } else setNotFound(true);
  }

  // Sempre renderizada: a ScreenDialog monta ao abrir e fica montada enquanto sai.
  return (
    <ScreenDialog
      open={open}
      icon="extension"
      title={tx('ext_title')}
      closeLabel={tx('close')}
      onClose={closeModal}
    >
      <p className="as-dialog__text">{tx('ext_text')}</p>
      <ol className="as-steps">
        {(['ext_s1', 'ext_s2', 'ext_s3'] as const).map((key, i) => (
          <li key={key}>
            <span aria-hidden="true">{i + 1}</span>
            <Html as="span" html={tx(key)} />
          </li>
        ))}
      </ol>
      <AbreNoLugar aberto={notFound}>
        <div className="as-note as-note--info" role="status">
          <span className="material-symbols-rounded" aria-hidden="true">
            info
          </span>
          <div>
            <strong>{tx('ext_missing_title')}</strong>
            <span>{tx('ext_missing_text')}</span>
            <button
              type="button"
              className="as-btn as-btn--outline as-btn--sm ds-resposta"
              onClick={() => window.location.reload()}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                refresh
              </span>
              {tx('reload_page')}
            </button>
          </div>
        </div>
      </AbreNoLugar>
      <div className="as-dialog__actions">
        <button
          type="button"
          className="as-btn as-btn--outline as-btn--md ds-resposta"
          aria-disabled={checking || undefined}
          aria-busy={checking || undefined}
          onClick={() => void handleRecheck()}
        >
          <RotuloCarregando
            ocupado={checking}
            feito={found}
            rotulo={tx('ext_installed')}
            rotuloOcupado={tx('ext_verifying')}
          />
        </button>
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--md ds-resposta"
          onClick={() => {
            emitActivity('extension.install_clicked', { source: 'modal' });
            window.open(brand.webStoreUrl, '_blank', 'noopener,noreferrer');
          }}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            open_in_new
          </span>
          {tx('ext_store')}
        </button>
      </div>
    </ScreenDialog>
  );
}
